import {ChecklistIcon, CommentDiscussionIcon, CopilotIcon, FileDiffIcon, GitCommitIcon} from '@primer/octicons-react'
import {CounterLabel} from '@primer/react'
import {TabNav} from '@primer/react/deprecated'
import {Link} from '@github-ui/react-core/link'
import {useLocation} from '@github-ui/react-router'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {clsx} from 'clsx'
import {useCallback} from 'react'
import {useTabCountsPageData} from '../page-data/loaders/use-tab-counts-page-data'
import type {NavigationCounterPageData} from '../page-data/payloads/tab-counts'
import {pullRequestsLayoutRoute} from '../routes/layout-route'
import type {NavigationUrls} from '../types/navigation-urls-types'
import styles from './PullRequestHeaderNavigation.module.css'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {HeaderRepository} from '../page-data/payloads/header'
import type {PullRequest} from '../types'
import {pullRequestsCommitsRoute} from '../routes/commits-route'
import {pullRequestsFilesRoute} from '../routes/files-route'
import {pullRequestsChangesRoute} from '../routes/changes-route'

interface PullRequestHeaderNavigationProps {
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
}

export function PullRequestHeaderNavigation({pullRequest, repository, urls}: PullRequestHeaderNavigationProps) {
  const isDataRouter = useIsDataRouterEnabled()
  if (isDataRouter) {
    return <PullRequestHeaderNavigationData pullRequest={pullRequest} repository={repository} urls={urls} />
  }
  return <PullRequestHeaderNavigationNavigator pullRequest={pullRequest} repository={repository} urls={urls} />
}

function PullRequestHeaderNavigationNavigator({pullRequest, repository, urls}: PullRequestHeaderNavigationProps) {
  const {data: labelCounts} = useTabCountsPageData()

  return (
    <PullRequestHeaderNavigationShared
      labelCounts={labelCounts}
      pullRequest={pullRequest}
      repository={repository}
      urls={urls}
    />
  )
}

function PullRequestHeaderNavigationData({pullRequest, repository, urls}: PullRequestHeaderNavigationProps) {
  const {data: labelCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')
  const combinedPrChangesRoute = useFeatureFlag('combined_pr_changes_route')

  return (
    <PullRequestHeaderNavigationShared
      labelCounts={labelCounts}
      pullRequest={pullRequest}
      repository={repository}
      urls={urls}
      combinedPrChangesRoute={combinedPrChangesRoute}
    />
  )
}

function PullRequestHeaderNavigationShared({
  pullRequest,
  repository,
  urls,
  labelCounts,
  combinedPrChangesRoute = false,
}: PullRequestHeaderNavigationProps & {
  labelCounts: NavigationCounterPageData | undefined
  pullRequest: PullRequest
  repository: HeaderRepository
  combinedPrChangesRoute?: boolean
}) {
  const location = useLocation()
  const isCurrentLocation = useCallback((url: string) => location.pathname === url, [location])
  const isCurrentLocationOrRange = useCallback(
    (url: string) => location.pathname === url || location.pathname.startsWith(`${url}/`),
    [location],
  )
  const tabClasses = `position-relative px-3 flex-shrink-0 text-normal ${styles.muteWhenUnselected} ${styles.overrideLineHeight}`

  const {commitsCount} = pullRequest

  const iconClasses = 'fg-muted mr-2 d-none d-sm-inline-block'

  const asyncCounterClasses = clsx('ml-2', labelCounts ? '' : styles.counterLoading)
  const walkthroughEnabled = isFeatureEnabled('hypersight')

  const fileLimit = labelCounts?.filesChangedCount ?? 0

  return (
    <TabNav aria-label="Pull request navigation tabs" data-turbo="false">
      <TabNav.Link
        href={urls.conversation}
        selected={isCurrentLocation(urls.conversation)}
        className={tabClasses}
        data-prefetch="eager"
      >
        <CommentDiscussionIcon className={iconClasses} />
        Conversation
        {typeof labelCounts?.conversationCount === 'number' && (
          <CounterLabel className={asyncCounterClasses}>{labelCounts.conversationCount}</CounterLabel>
        )}
      </TabNav.Link>
      <TabNav.Link
        as={Link}
        to={pullRequestsCommitsRoute.generatePath({
          owner: repository.ownerLogin,
          repo: repository.name,
          pr_number: pullRequest.number.toString(),
        })}
        selected={isCurrentLocationOrRange(urls.commits)}
        className={tabClasses}
        id="prs-commits-anchor-tab"
      >
        <GitCommitIcon className={iconClasses} />
        Commits
        {typeof commitsCount === 'number' && <CounterLabel className="ml-2">{commitsCount}</CounterLabel>}
      </TabNav.Link>
      <TabNav.Link
        href={urls.checks}
        selected={isCurrentLocation(urls.checks)}
        className={tabClasses}
        data-prefetch="moderate"
      >
        <ChecklistIcon className={iconClasses} />
        Checks
        {typeof labelCounts?.checksCount === 'number' && (
          <CounterLabel className={asyncCounterClasses}>{labelCounts.checksCount}</CounterLabel>
        )}
      </TabNav.Link>
      <TabNav.Link
        as={Link}
        id="prs-files-anchor-tab"
        to={
          combinedPrChangesRoute
            ? pullRequestsChangesRoute.generatePath({
                owner: repository.ownerLogin,
                repo: repository.name,
                pr_number: pullRequest.number.toString(),
              })
            : pullRequestsFilesRoute.generatePath({
                owner: repository.ownerLogin,
                repo: repository.name,
                pr_number: pullRequest.number.toString(),
              })
        }
        selected={isCurrentLocationOrRange(urls.files) || isCurrentLocationOrRange(urls.changes)}
        className={tabClasses}
      >
        <FileDiffIcon className={iconClasses} />
        Files changed
        {labelCounts && labelCounts.filesChangedCount ? (
          <CounterLabel className={asyncCounterClasses}>
            {`${fileLimit}${labelCounts?.filesChangedCountLimitExceeded ? '+' : ''}`}
          </CounterLabel>
        ) : null}
      </TabNav.Link>
      {walkthroughEnabled && (
        <TabNav.Link
          href={urls.walkthrough}
          selected={isCurrentLocation(urls.walkthrough)}
          className={tabClasses}
          data-prefetch="moderate"
        >
          <CopilotIcon className={iconClasses} />
          Walkthrough
        </TabNav.Link>
      )}
    </TabNav>
  )
}

try{ PullRequestHeaderNavigation.displayName ||= 'PullRequestHeaderNavigation' } catch {}
try{ PullRequestHeaderNavigationNavigator.displayName ||= 'PullRequestHeaderNavigationNavigator' } catch {}
try{ PullRequestHeaderNavigationData.displayName ||= 'PullRequestHeaderNavigationData' } catch {}
try{ PullRequestHeaderNavigationShared.displayName ||= 'PullRequestHeaderNavigationShared' } catch {}